import { isEditable } from '../../lib/blocks'
import type { Block } from '../../lib/types'

/**
 * Undo for the composer's block operations (Niki, 10/2: "undo for moving,
 * resizing and cropping images" — the launch blocker).
 *
 * Each entry is one discrete operation: the blocks array just before it and
 * just after it. A drag or a resize is one entry however many steps it took
 * live: a gesture opens an entry, every step lands in it, and its end closes
 * it. Typing is not in here at all — it keeps the browser's own undo (the
 * key rule is in Composer.tsx, `useBlockUndo`).
 *
 * Undo doesn't put the old array back wholesale: it rebases the operation
 * off whatever the page holds now, so words typed since an image moved stay
 * when the move is undone (`rebase`).
 */
export interface HistoryEntry {
  before: Block[]
  after: Block[]
  /** Text had changed since the entry before this one: once this is undone, ⌘Z in a paragraph is the browser's again. */
  textBefore: boolean
  /** A gesture still going (a drag): its steps keep landing in this entry. */
  open?: boolean
  /** The gesture has let go; it closes at `settle`. */
  ending?: boolean
}

export const HISTORY_LIMIT = 100

export class BlockHistory {
  undoStack: HistoryEntry[] = []
  redoStack: HistoryEntry[] = []
  /** Set for the rest of the current task after a record, so two writes from one release (a column's width, then the image's) are one entry. */
  private joining = false
  /** Where the top entry's writes started from: a replay of one (Strict Mode renders twice) starts from one of these again. */
  private froms = new Set<Block[]>()

  constructor(private limit = HISTORY_LIMIT) {}

  get canUndo() {
    return this.undoStack.some((e) => !e.open || e.after !== e.before)
  }
  get canRedo() {
    return this.redoStack.length > 0
  }
  private get top(): HistoryEntry | undefined {
    return this.undoStack[this.undoStack.length - 1]
  }

  private push(e: HistoryEntry) {
    this.froms = new Set()
    this.undoStack.push(e)
    if (this.undoStack.length > this.limit) this.undoStack.splice(0, this.undoStack.length - this.limit)
    this.redoStack = []
  }

  /**
   * One write of a block operation, `before` → `after`. It continues the top
   * entry when that's an open gesture (and this write is one of its steps,
   * `inGesture`), or another write from this same task, picking up where it
   * left off; React running the same updater twice (Strict Mode) replays onto
   * the same `before` and only refreshes `after`.
   *
   * A write from outside the gesture — a box dropped by its grip, an insert —
   * closes a gesture that's still open first, so it's an undo of its own.
   * (Niki, 10/2: a hand drag that let go with nothing left to write stayed
   * open until the next render, and the next move was folded into it: one ⌘Z
   * took both back, and the next found nothing.)
   */
  record(before: Block[], after: Block[], textBefore: boolean, inGesture = false) {
    if (after === before) return
    if (!inGesture && this.top?.open) this.close()
    const top = this.top
    if (top && (top.open || this.joining) && (top.after === before || this.froms.has(before))) top.after = after
    else this.push({ before, after, textBefore })
    this.froms.add(before)
    if (!this.joining) {
      this.joining = true
      queueMicrotask(() => {
        this.joining = false
      })
    }
  }

  /** A drag or resize began, from `blocks`: everything until `end` is one entry. */
  begin(blocks: Block[], textBefore: boolean) {
    this.end()
    this.settle()
    this.push({ before: blocks, after: blocks, textBefore, open: true })
  }

  /**
   * The gesture let go. Its last step may still be on its way into the
   * document (React applies it at the next render), so the entry stays open
   * until `settle`, which the composer calls once the document has rendered.
   */
  end() {
    const top = this.top
    if (top?.open) top.ending = true
  }

  /** Close a gesture that has let go; one that came back where it started leaves nothing to undo. */
  settle() {
    if (this.top?.ending) this.close()
  }

  /** Close the open gesture now, let go or not (one nobody is holding any more). */
  close() {
    const top = this.top
    if (!top?.open) return
    top.open = false
    top.ending = false
    this.joining = false
    if (top.after === top.before) this.undoStack.pop()
  }

  /** The gesture was called off (Esc): its entry goes, and this is what to put back. */
  cancel(): Block[] | null {
    const top = this.top
    if (!top?.open) return null
    this.undoStack.pop()
    return top.before
  }

  /** Take the latest entry to undo (null when there's none, or a gesture is under way). */
  undo(): HistoryEntry | null {
    this.settle()
    if (this.top?.open) return null
    const e = this.undoStack.pop()
    if (!e) return null
    this.froms = new Set()
    this.redoStack.push(e)
    return e
  }

  redo(): HistoryEntry | null {
    this.settle()
    if (this.top?.open) return null
    const e = this.redoStack.pop()
    if (!e) return null
    this.froms = new Set()
    this.undoStack.push(e)
    return e
  }

  clear() {
    this.froms = new Set()
    this.undoStack = []
    this.redoStack = []
  }
}

/**
 * Apply the change `from` → `to` on top of `current`, block by block. A block
 * the operation changed (or made, or removed) takes its `to` version; one it
 * left alone keeps whatever it is now — so a paragraph typed into since the
 * operation keeps its words, and one deleted since stays deleted. Blocks
 * made since (a new paragraph typed after the move) stay after the block
 * they followed. Order is `to`'s.
 */
export function rebase(current: Block[], from: Block[], to: Block[]): Block[] {
  const fromById = new Map(from.map((b) => [b.id, b]))
  const toIds = new Set(to.map((b) => b.id))
  const nowById = new Map(current.map((b) => [b.id, b]))
  const out: Block[] = []
  for (const b of to) {
    if (fromById.get(b.id) !== b) out.push(b)
    else {
      const now = nowById.get(b.id)
      if (now) out.push(now)
    }
  }
  const placed = new Set(out.map((b) => b.id))
  current.forEach((b, i) => {
    if (placed.has(b.id) || fromById.has(b.id) || toIds.has(b.id)) return
    let j = i - 1
    while (j >= 0 && !placed.has(current[j].id)) j--
    const at = j < 0 ? 0 : out.findIndex((x) => x.id === current[j].id) + 1
    out.splice(at, 0, b)
    placed.add(b.id)
  })
  return out
}

/**
 * Text blocks whose words or kind an undo changed under the caret: their
 * contenteditable only reads its HTML when it mounts, so they need a remount.
 */
export function changedEditables(before: Block[], after: Block[]): string[] {
  const was = new Map(before.map((b) => [b.id, b]))
  const out: string[] = []
  for (const b of after) {
    const w = was.get(b.id)
    if (!w || w === b) continue
    if (isEditable(b) && (!isEditable(w) || w.type !== b.type || w.html !== b.html || headingOf(w) !== headingOf(b))) out.push(b.id)
  }
  return out
}

const headingOf = (b: Block) => ('level' in b ? b.level : undefined)

/**
 * Whose ⌘Z it is. Typing keeps the browser's own undo: in a text field (a
 * paragraph, the title, a caption or note box) ⌘Z is ours only while no text
 * has changed since the last block operation — so a paragraph's typing is
 * never undone by us, and ours never by the browser. ⇧⌘Z there is ours only
 * straight after one of our undos. Anywhere else on the page (a selected
 * image, the page itself) both are always ours.
 */
export function undoOwner(
  key: { kind: 'undo' | 'redo'; inTextField: boolean },
  state: { textSinceOp: boolean; lastWasUndo: boolean; canUndo: boolean; canRedo: boolean },
): 'ours' | 'browser' {
  if (key.kind === 'undo') {
    if (key.inTextField && state.textSinceOp) return 'browser'
    return state.canUndo ? 'ours' : 'browser'
  }
  if (key.inTextField && !state.lastWasUndo) return 'browser'
  return state.canRedo ? 'ours' : 'browser'
}

/** ⌘Z / Ctrl+Z is undo; ⇧⌘Z, Ctrl+⇧Z and Ctrl+Y are redo. */
export function undoKey(e: { key: string; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; altKey: boolean }): 'undo' | 'redo' | null {
  if (e.altKey || !(e.metaKey || e.ctrlKey)) return null
  const k = e.key.toLowerCase()
  if (k === 'z') return e.shiftKey ? 'redo' : 'undo'
  if (k === 'y' && e.ctrlKey && !e.metaKey && !e.shiftKey) return 'redo'
  return null
}

export function isTextField(el: Element | null): boolean {
  if (!el) return false
  if ((el as HTMLElement).isContentEditable) return true
  return !!el.closest?.('input, textarea, select, [contenteditable="true"], [contenteditable=""]')
}
